import { Button, Notification } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import { scrollToTop } from "apps/commons/functions/scrollToTop";
import { amenitiesBinder } from "libs/analytics/binders/onboarding/amenities";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { type FunctionComponent, useEffect, useState } from "react";
import { Title } from "../Title";
import { useMetadataAtomValue } from "../metadataAtom";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { AmenityGroup } from "./AmenityGroup";
import { useAmenitiesHandler } from "./useAmenitiesHandler";

export const AmenitiesPage: FunctionComponent = () => {
    const {
        amenitiesGroups,
        cmsData: {
            amenity: {
                amenities,
                pleaseSelectAtLeast3Amenities,
                showAllAmenities,
                showLess,
                guestsFavorites,
                travelersPreferTheseAmenitiesWhenBookingAPlaceToStay,
                propertiesWithMoreAmenitiesTendToReceiveMoreViews,
            },
        },
    } = useStaticDataAtomValue();
    const {
        experimentsAtom: { isConvergenceAmenities },
    } = useMetadataAtomValue();

    const [showMore, setShowMore] = useState(false);
    const { activeIds, handleToggleAmenity, notification } = useAmenitiesHandler(isConvergenceAmenities);

    const amenityGroupIdsToTop = [10];
    const handleToggleShowMore = () => setShowMore((o) => !o);

    const amenityGroupBaseProps = {
        activeIds,
        onClick: handleToggleAmenity,
    };

    useEffect(() => {
        scrollToTop();
    }, []);

    useAnalytics(amenitiesBinder);

    if (isConvergenceAmenities) {
        const amenityItems = amenitiesGroups.map((group) => group.amenities).flat();
        const favoriteAmenityGroupTypeId = 10;

        return (
            <VStack gap="24" pb="32">
                <Title title={amenities} />
                <AmenityGroup
                    amenitiesGroupType={favoriteAmenityGroupTypeId}
                    key={`amenity-group`}
                    title={guestsFavorites}
                    description={travelersPreferTheseAmenitiesWhenBookingAPlaceToStay}
                    amenities={amenityItems}
                    {...amenityGroupBaseProps}
                />
                <Notification
                    intent="notice"
                    title={propertiesWithMoreAmenitiesTendToReceiveMoreViews}
                    asset={{
                        id: "fill.symbol.circle-info",
                    }}
                />
            </VStack>
        );
    }

    return (
        <VStack gap="24" pb="32">
            <Title title={amenities} />
            {notification && (
                <Notification
                    intent={notification}
                    title={pleaseSelectAtLeast3Amenities}
                    asset={{
                        id: notification === "notice" ? "fill.symbol.circle-info" : "fill.symbol.triangle-exclamation",
                    }}
                />
            )}
            {amenityGroupIdsToTop.map((groupId, index) => (
                <AmenityGroup
                    key={`amenity-group-top-${groupId}`}
                    {...amenityGroupBaseProps}
                    divider={index !== 0}
                    {...amenitiesGroups.find((a) => a.amenitiesGroupType === groupId)}
                />
            ))}
            {showMore &&
                amenitiesGroups.map(
                    (group) =>
                        !amenityGroupIdsToTop.includes(group.amenitiesGroupType) && (
                            <AmenityGroup
                                key={`amenityGroup-${group.amenitiesGroupType}`}
                                divider
                                {...amenityGroupBaseProps}
                                {...group}
                            />
                        ),
                )}
            <Button
                variant="label"
                hierarchy="tertiary"
                size="m"
                label={!showMore ? showAllAmenities : showLess}
                leadingIcon={!showMore ? "fill.arrow.thin-dropdown" : "fill.arrow.thin-collapse"}
                onClick={handleToggleShowMore}
                data-element-name={!showMore ? "show-all-amenities" : "show-less-amenities"}
            />
        </VStack>
    );
};
